import { EventEmitter } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { Tab } from '../../model/tabs/tab';

export class StandardTabManager<T extends Tab> {
  public tabs: T[];
  protected resizeEmitter = new EventEmitter();

  protected activeTab$$ = new BehaviorSubject<T | null>(null);
  public readonly activeTab$ = this.activeTab$$.asObservable();

  protected closeTab$$ = new Subject<T>();
  public readonly closeTab$ = this.closeTab$$.asObservable();

  protected _index = 0;

  constructor(tabs: T[] = []) {
    this.tabs = tabs;
  }

  public get resize(): Observable<any> {
    return this.resizeEmitter.asObservable();
  }

  public get activeTab(): T | null {
    return this.activeTab$$.getValue();
  }

  /**
   * Return the index of active Tab
   */
  get index(): number {
    return this._index;
  }

  /**
   * Active an other tab
   * @param tabId
   */
  public openTab(tabId: string): void {
    this._index = this.findIndexById(tabId);
    this.activeTab$$.next(this.tabs[this._index]);
  }

  /**
   * Add new tab on the TabContainer if the tab.id doesn't exist.
   * Return true if the tab is added, false if the tab already exist.
   * @param tab instance of {@link Tab}
   */
  public addTab(tab: T, openTab = true): boolean {
    const index = this.findIndexById(tab.id);
    if (index !== -1) {
      if (openTab) {
        this.openTab(tab.id);
      }
      return false;
    }

    if (this.activeTab) {
      const currentIndex = this.findIndexById(this.activeTab.id);
      if (currentIndex === -1) {
        this.tabs.push(tab);
      } else {
        this.tabs.splice(currentIndex + 1, 0, tab);
      }
    } else {
      this.tabs.push(tab);
    }

    if (tab.isOpen) {
      if (openTab) {
        this.openTab(tab.id);
      }
    }
    this.resizeEmitter.emit();
    return true;
  }

  /**
   * Hide a tab
   * @param id equals tab.id
   */
  public hide(id: string) {
    const tab = this.tabs.find((dt) => dt.id === id);
    if (tab) {
      this.switchActiveTab(tab);
      tab.enable = false;
    }
  }

  /**
   * Display a tab
   * @param id equals tab.id
   */
  public show(id: string) {
    const tab = this.tabs.find((dt) => dt.id === id);
    if (tab) {
      tab.enable = true;
    }
  }

  /**
   * Close Tab. If the tab is the active Tab, shift to activate an other tab.
   * @param tab
   */
  public closeTab(tab: T): void {
    if (!tab || this.tabs.length === 0) {
      return;
    }

    this.switchActiveTab(tab);
    const index = this.findIndexById(tab.id);
    this.tabs.splice(index, 1);

    if (this.activeTab === null) {
      this._index = -1;
    } else {
      this._index = this.findIndexById(this.activeTab.id);
    }

    this.resizeEmitter.emit();
    this.closeTab$$.next(tab);
    return;
  }

  /**
   * Close active Tab.
   */
  public closeActiveTab(): void {
    this.closeTab(this.activeTab);
  }

  /**
   * Check if the tab is the activeTab
   * @param tab instance of {@link Tab}
   */
  public isActive(tab: T): boolean {
    return tab === this.activeTab;
  }

  private switchActiveTab(tab: T): void {
    if (!this.isActive(tab)) {
      return;
    }

    if (this.tabs.length === 1) {
      this.activeTab$$.next(null);
      return;
    }

    if (this.tabs.length > 1 && this.index === 0) {
      this.openTab(this.tabs.at(1).id);
      return;
    }

    if (this.tabs.length > 1 && this.index >= 1) {
      this.openTab(this.tabs.at(this.index - 1).id);
      return;
    }

    return;
  }

  public moveTab(fromIndex: number, toIndex: number) {
    if (fromIndex < 0 || fromIndex >= this.tabs.length) {
      throw new Error('Index out of range');
    }
    if (toIndex < 0 || toIndex >= this.tabs.length) {
      throw new Error('Index out of range');
    }
    if (fromIndex === toIndex) {
      return;
    }
    const tab = this.tabs[fromIndex];
    this.tabs.splice(fromIndex, 1);
    this.tabs.splice(toIndex, 0, tab);
    this.resizeEmitter.emit();
  }

  private findIndexById(tabId: string) {
    return this.tabs.findIndex((tab) => tab.id === tabId);
  }
}
